import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  MapIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import styled from "styled-components";

import { Header, Link, PageWrapper } from "src/shared/components";
import { useEventContext } from "src/shared/contexts/EventContext";

import {
  MENTOR_CAFE_COPY,
  MENTOR_CAFE_INFO_CARDS,
  MENTOR_CAFE_ROTATIONS,
  MentorCafeInfoCardId,
} from "./constants";
import MentorHeadshot from "./MentorHeadshot";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  width: 100%;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IntroTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 28px;
  font-weight: 900;
  line-height: 1.2;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 24px;
  `}

  ${({ theme }) => theme.mediaQueries.smallMobile`
    font-size: 20px;
  `}
`;

const RotationTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-weight: 900;
  line-height: 1.2;
`;

const Body = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    grid-template-columns: 1fr;
  `}
`;

const InfoCard = styled.article`
  display: flex;
  min-width: 0;
  min-height: 128px;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const InfoHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
    stroke-width: 1.8;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
`;

const RotationHeading = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  `}
`;

const RotationDivider = styled.span`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.globalConstants.color.borderGray};

  ${({ theme }) => theme.mediaQueries.largeMobile`
    display: none;
  `}
`;

const RotationTime = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-weight: 700;
`;

const MentorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 168px);
  gap: 24px;
  width: 100%;
`;

const MentorCard = styled.article`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
`;

const MentorDetails = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
`;

const MentorName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.375;
  overflow-wrap: anywhere;
`;

const MentorTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  line-height: 1.375;
  overflow-wrap: anywhere;
`;

const INFO_CARD_ICONS: Record<
  MentorCafeInfoCardId,
  React.ComponentType<React.ComponentPropsWithoutRef<"svg">>
> = {
  location: MapIcon,
  hours: ClockIcon,
  "walk-in": UserGroupIcon,
  conversation: ChatBubbleLeftRightIcon,
};

const HackerMentorCafe: React.FC = () => {
  const { mentorCafe } = useEventContext();
  const categoryAliases: Record<string, string> = {
    software: "software-generalists",
    other: "mixed-open-overflow",
  };
  return (
    <>
      <Header
        title={MENTOR_CAFE_COPY.headerTitle.text}
        subtitle={MENTOR_CAFE_COPY.headerSubtitle.text}
        data-title-copy-status={MENTOR_CAFE_COPY.headerTitle.status}
        data-subtitle-copy-status={MENTOR_CAFE_COPY.headerSubtitle.status}
      />
      <PageWrapper fullWidth stretchHeight={false}>
        <Content>
          <Section aria-labelledby="mentor-cafe-intro-title">
            <Intro>
              <IntroTitle
                id="mentor-cafe-intro-title"
                data-copy-status={MENTOR_CAFE_COPY.introTitle.status}
              >
                {MENTOR_CAFE_COPY.introTitle.text}
              </IntroTitle>
              <Body data-copy-status={MENTOR_CAFE_COPY.introBody.status}>
                {MENTOR_CAFE_COPY.introBody.text}
              </Body>
            </Intro>
            <InfoGrid>
              {MENTOR_CAFE_INFO_CARDS.map(({ id, title, body }) => {
                const Icon = INFO_CARD_ICONS[id];
                return (
                  <InfoCard key={id}>
                    <InfoHeading>
                      <CardTitle data-copy-status={title.status}>
                        {title.text}
                      </CardTitle>
                      <Icon aria-hidden="true" />
                    </InfoHeading>
                    <Body data-copy-status={body.status}>{body.text}</Body>
                  </InfoCard>
                );
              })}
            </InfoGrid>
          </Section>

          {MENTOR_CAFE_ROTATIONS.map((rotation) => (
            <Section
              key={rotation.id}
              aria-labelledby={`mentor-cafe-${rotation.id}`}
            >
              <RotationHeading>
                <RotationTitle
                  id={`mentor-cafe-${rotation.id}`}
                  data-copy-status={rotation.title.status}
                >
                  {rotation.title.text}
                </RotationTitle>
                <RotationDivider aria-hidden="true" />
                <RotationTime data-copy-status={rotation.time.status}>
                  {rotation.time.text}
                </RotationTime>
              </RotationHeading>
              <Body data-copy-status={rotation.description.status}>
                {rotation.description.text}
              </Body>
              <MentorGrid role="list">
                {mentorCafe.mentors
                  .filter((mentor) =>
                    mentor.categoryIds.some(
                      (id) => (categoryAliases[id] ?? id) === rotation.id
                    )
                  )
                  .map((mentor) => (
                    <MentorCard key={mentor.id} role="listitem">
                      <MentorHeadshot
                        url={mentor.headshotUrl}
                        name={mentor.name}
                      />
                      <MentorDetails>
                        <MentorName>
                          {mentor.profileUrl ? (
                            <Link
                              href={mentor.profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {mentor.name}
                            </Link>
                          ) : (
                            mentor.name
                          )}
                        </MentorName>
                        {mentor.pronouns && (
                          <MentorTitle>{mentor.pronouns}</MentorTitle>
                        )}
                        <MentorTitle>{mentor.title}</MentorTitle>
                      </MentorDetails>
                    </MentorCard>
                  ))}
              </MentorGrid>
            </Section>
          ))}
        </Content>
      </PageWrapper>
    </>
  );
};

export default HackerMentorCafe;
